// Qué etiquetas de unidad caben en el mapa sin encimarse (pura, con tests).
//
// Con «Nombres de unidades» encendido cada marcador pintaba su etiqueta: con 40
// unidades a ~1 km entre sí, a zoom de ciudad, quedaba una columna de 20
// etiquetas montadas unas sobre otras donde no se leía ninguna. Como hacen
// Mapon y Google Maps con sus rótulos: se dibuja la que cabe y la que chocaría
// con una ya puesta se omite. Al acercar, se separan y aparecen.

import { shortenName } from "./shortenName";
import type { UnitDisplaySettings } from "./types";

export interface LabelCandidate {
  id: string;
  lat: number;
  lng: number;
  name: string;
  speed?: string | null;
  /** Mayor = se pinta primero (la unidad seguida, las que se mueven). */
  priority: number;
}

// UnitLabel tiene max-w-[200px]. El ancho real depende de la fuente; esta
// estimación (0.62 em por carácter en semibold, más padding, punto y piquito)
// queda un poco por encima del real a propósito: mejor omitir una etiqueta de
// más que dejar dos encimadas.
const MAX_W = 200;
const GAP = 4;

export function estimateLabelSize(
  name: string,
  speed: string | null | undefined,
  s: Pick<UnitDisplaySettings, "label_px" | "speed_px" | "show_speed" | "name_mode" | "status_dot">,
): { w: number; h: number } {
  const text = s.name_mode === "none" ? "" : s.name_mode === "unit" ? shortenName(name) : name;
  let w = 16 + text.length * s.label_px * 0.62;
  if (s.show_speed && speed) w += 6 + speed.length * s.speed_px * 0.6;
  if (s.status_dot) w += 14;
  return { w: Math.min(MAX_W, w), h: s.label_px + 10 };
}

// Píxeles de mundo en Web Mercator (tiles de 256 px), los mismos que usa
// Google Maps: la distancia en pantalla entre dos unidades a un zoom dado.
export function project(lat: number, lng: number, zoom: number): { x: number; y: number } {
  const scale = 256 * 2 ** zoom;
  const s = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
  return {
    x: ((lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * scale,
  };
}

/**
 * Ids cuyas etiquetas se pintan. Codicioso por prioridad; una rejilla de
 * celdas de 200 px deja cada choque en O(1), así que con 1,000+ unidades se
 * recalcula en cada reporte del socket sin trabar el mapa.
 */
export function visibleLabelIds(
  candidates: LabelCandidate[],
  zoom: number,
  settings: Pick<UnitDisplaySettings, "label_px" | "speed_px" | "show_speed" | "name_mode" | "status_dot">,
): Set<string> {
  const out = new Set<string>();
  const cells = new Map<string, Array<[number, number, number, number]>>();
  const C = MAX_W;
  const sorted = candidates
    .map((c, i) => ({ c, i }))
    .sort((a, b) => b.c.priority - a.c.priority || a.i - b.i);

  for (const { c } of sorted) {
    if (!Number.isFinite(c.lat) || !Number.isFinite(c.lng)) continue;
    const { x, y } = project(c.lat, c.lng, zoom);
    const { w, h } = estimateLabelSize(c.name, c.speed, settings);
    // Todas van encima de su marcador con el mismo despegue: basta comparar
    // el rectángulo relativo al punto.
    const box: [number, number, number, number] = [x - w / 2 - GAP, y - h - GAP, x + w / 2 + GAP, y + GAP];
    const keys: string[] = [];
    for (let cx = Math.floor(box[0] / C); cx <= Math.floor(box[2] / C); cx++)
      for (let cy = Math.floor(box[1] / C); cy <= Math.floor(box[3] / C); cy++) keys.push(`${cx}:${cy}`);

    const hit = keys.some((k) =>
      (cells.get(k) ?? []).some((o) => box[0] < o[2] && box[2] > o[0] && box[1] < o[3] && box[3] > o[1]),
    );
    if (hit) continue;
    out.add(c.id);
    for (const k of keys) {
      const list = cells.get(k);
      if (list) list.push(box);
      else cells.set(k, [box]);
    }
  }
  return out;
}
